/**
 * The panel's own portal target: the `.sphere-ai-thread` element.
 *
 * Everything that makes this panel look like the platform — the shadcn colour tokens remapped to
 * the MDOTM palette, the type scale, the single corner radius — is declared on `.sphere-ai-thread`
 * and reaches its subtree by inheritance (see styles/customizations.css). A floating surface that
 * portals to `document.body` is therefore NOT covered by any of it, however carefully it is built:
 * it lands outside the subtree and falls back to the app-wide defaults.
 *
 * So a popover/dropdown/tooltip inside the panel should portal INTO the panel. This context carries
 * the element to hand to Radix's `container` (`PopoverContent`'s `container` prop and its
 * equivalents), which exists for exactly this.
 *
 * An element in state rather than a ref object, so a consumer re-renders once the panel has
 * mounted — a ref's `.current` changing would not notify anyone. `null` until then, which is also
 * what a consumer should pass through: Radix reads `null`/`undefined` as "default target", so a
 * surface used outside the panel keeps working instead of failing to mount.
 */
import { createContext, useContext } from "react";

const SphereAIPanelPortalContext = createContext<HTMLElement | null>(null);

export const SphereAIPanelPortalProvider = SphereAIPanelPortalContext.Provider;

/** The panel element to portal floating surfaces into, or `null` outside the panel. */
export function useSphereAIPanelPortal(): HTMLElement | null {
	return useContext(SphereAIPanelPortalContext);
}
